import axios from "axios";
import {useState, useRef} from "react";
import {ScrollPanel} from "primereact/scrollpanel";

import {Calendar} from "primereact/calendar";
import {AutoComplete} from "primereact/autocomplete";
import {Button} from "primereact/button";
import {base, projects_url, coworker_url} from "../../../api";

export default function ReportGeneration({item}) {
	const [formValues, setFormValues] = useState({});
	const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState({});
	let reportingVariables = [];
	try {
		reportingVariables = JSON.parse(item?.form_fields || "[]");
	} catch (e) {
		reportingVariables = [];
	}

	const [previewData, setPreviewData] = useState({});

	const debounceRef = useRef(null);

	const handleChange = (type, value) => {
		setFormValues(prev => {
			const updated = {...prev, [type]: value};

			// Clear previous debounce timeout
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			// Set new debounce
			debounceRef.current = setTimeout(() => {
				getPreviewData(updated);
			}, 500);

			return updated;
		});
	};

	const getFetchUrl = type => {
		if (type === "project_id") return base + projects_url;
		if (type === "employee_id") return base + coworker_url;
		return "";
	};

	const handleAutoComplete = async (e, type) => {
		const query = e.query.toLowerCase();
		const url = getFetchUrl(type);
		if (!url) return;

		try {
			const res = await axios.get(url);
			const data = res.data || [];
			const filtered = data.filter(item =>
				Object.values(item).some(val => String(val).toLowerCase().includes(query)),
			);
			setAutoCompleteSuggestions(prev => ({
				...prev,
				[type]: filtered,
			}));
		} catch (error) {
			console.error("Failed to fetch suggestions", error);
		}
	};

	const renderField = variable => {
		switch (variable.type) {
			case "from_date":
				return (
					<Calendar
						value={formValues[variable.type] || null}
						onChange={e => handleChange(variable.type, e.value)}
						className="w-100"
						showIcon
					/>
				);
			case "to_date":
				return (
					<Calendar
						value={formValues[variable.type] || null}
						onChange={e => handleChange(variable.type, e.value)}
						className="w-100"
						showIcon
					/>
				);
			case "project_id":
				return (
					<AutoComplete
						id={variable.type}
						value={formValues[variable.type] || ""}
						suggestions={autoCompleteSuggestions[variable.type] || []}
						completeMethod={e => handleAutoComplete(e, variable.type)}
						field="full_name_am"
						onChange={e => handleChange(variable.type, e.value)}
						placeholder={`Search ${variable.type}`}
						dropdown
						className="w-100"
						panelStyle={{width: "300px"}}
						itemTemplate={item => (
							<>
								<div>
									<div className="text-dark">{item.full_name_am}</div>
									<small className="text-primary">{item.name || "No code available"}</small>
								</div>
								<hr className="m-0 p-0" />
							</>
						)}
					/>
				);
			case "employee_id":
				return (
					<AutoComplete
						id={variable.type}
						value={formValues[variable.type] || ""}
						suggestions={autoCompleteSuggestions[variable.type] || []}
						completeMethod={e => handleAutoComplete(e, variable.type)}
						field="partner.full_name_am"
						onChange={e => handleChange(variable.type, e.value)}
						placeholder={`Search ${variable.type}`}
						dropdown
						className="w-100"
						panelStyle={{width: "300px"}}
						itemTemplate={item => (
							<>
								<div className="">
									<div className="text-primary">
										{item.full_name_am || item?.partner.full_name_am}
									</div>
									<small className="text-primary">
										<span className="text-dark me-2">{item?.partner.phone || "not avaible"},</span>
										<span className="text-dark">{item?.partner.email || "not avaible"}</span>
									</small>
								</div>
								<hr className="m-0 p-0" />
							</>
						)}
					/>
				);
			default:
				return null;
		}
	};

	const downloadUrl = () => {
		const fromDate = formValues.from_date?.toISOString?.().slice(0, 10) || "";
		const toDate = formValues.to_date?.toISOString?.().slice(0, 10) || "";
		const projectId = formValues.project_id?.id || "";
		const employeeId = formValues.employee_id?.id || "";

		return `https://azk.webtest.am:444/reports/report-types/${item?.id}/download-report/?from_date=${fromDate}&to_date=${toDate}&project_id=${projectId}&employee_id=${employeeId}`;
	};

	const getPreviewData = (customFormValues = formValues) => {
		const fromDate = customFormValues.from_date?.toISOString?.().slice(0, 10) || "";
		const toDate = customFormValues.to_date?.toISOString?.().slice(0, 10) || "";
		const projectId = customFormValues.project_id?.id || "";
		const employeeId = customFormValues.employee_id?.id || "";

		const url = `https://azk.webtest.am:444/reports/report-types/${item?.id}/view-report/?from_date=${fromDate}&to_date=${toDate}&project_id=${projectId}&employee_id=${employeeId}`;
		axios
			.get(url)
			.then(res => {
				setPreviewData(res.data);
			})
			.catch(error => {
				console.error("Failed to fetch preview data", error);
			});
	};

	return (
		<div className="p-fluid">
			<div className="row">
				{reportingVariables.map((variable, index) => (
					<div key={index} className="col-lg-6 col-12">
						<div className="field mb-4">
							<label className="font-bold mb-2 d-block">{variable.description}</label>
							{renderField(variable)}
						</div>
					</div>
				))}
			</div>

			{previewData && <span className="fs-6 fw-bolder pb-3">Preview data</span>}
			<ScrollPanel style={{width: "100%", height: "200px"}} className="mt-3">
				{previewData &&
					(Object.keys(previewData).length > 0 ? (
						<div className="d-flex flex-column">
							{Object.entries(previewData).map(([key, value], index) => (
								<div key={index} className="mb-3">
									<div className="fw-bold text-primary">{key}</div>
									{Array.isArray(value) ? (
										value.map((item, idx) => (
											<div
												key={idx}
												className="ms-3"
												style={{
													backgroundColor: "white",
													marginBottom: "40px",
													borderTop: "4px solid #ccc",
													paddingTop: "10px",
													marginTop: "10px",
												}}>
												{typeof item === "object" && item !== null ? (
													Object.entries(item).map(([subKey, subValue], subIdx) => (
														<div
															key={subIdx}
															className="d-flex justify-content-between border-bottom py-1 mb-3 px-1">
															<span className="text-muted">{subKey}:</span>
															<span className="ms-2">{String(subValue)}</span>
														</div>
													))
												) : (
													<div className="text-secondary">{String(item)}</div>
												)}
											</div>
										))
									) : typeof value === "object" && value !== null ? (
										Object.entries(value).map(([subKey, subValue], subIdx) => (
											<div
												key={subIdx}
												className="d-flex justify-content-between ms-3 border-bottom">
												<span className="text-muted">{subKey}:</span>
												<span className="ms-2">{String(subValue)}</span>
											</div>
										))
									) : (
										<div className="text-muted fst-italic ms-2">{String(value)}</div>
									)}
								</div>
							))}
						</div>
					) : (
						<p>No preview data available</p>
					))}
			</ScrollPanel>

			{reportingVariables.length != 0 && <hr />}

			<div className="d-flex justify-content-center align-items-center mt-3 mb-2">
				<a
					href={downloadUrl()}
					target="_blank"
					rel="noopener noreferrer"
					className="col-4 d-flex justify-content-center text-center align-items-center mx-3">
					<Button label="Download" severity="success" rounded icon="pi pi-download" />
				</a>
			</div>
		</div>
	);
}
